/**
 * Passkey client for the staff CRM (WebAuthn).
 *
 * Ported from web_account's `lib/security.ts`, login half plus enrolment. The
 * CRM needs its own copy of the enrolment half, not only the login button:
 * a passkey belongs to one domain, so a key made on `account.husly.app` cannot
 * sign in on `crm.webadmin.ee`. Without a way to enrol here, a login button
 * would be a button nobody could ever use.
 */
import {
  startRegistration,
  startAuthentication,
  browserSupportsWebAuthn,
} from "@simplewebauthn/browser";
import { api } from "./api";
import type { LoginResult } from "./auth";

export interface Passkey {
  id: string;
  deviceName: string | null;
  aaguid: string | null;
  backupEligible: boolean;
  backupState: boolean;
  lastUsedAt: string | null;
  createdAt: string;
}

/** Thrown when the browser cannot run a ceremony at all. */
export class WebAuthnUnsupportedError extends Error {}

/**
 * A passkey needs a platform authenticator (Touch ID, Face ID, Windows Hello,
 * an Android fingerprint) plus a secure context (HTTPS, or localhost).
 */
export function isWebAuthnReady(): boolean {
  return typeof window !== "undefined" && window.isSecureContext && browserSupportsWebAuthn();
}

/**
 * A cancel or a timeout is not a failure worth an error banner: the person
 * pressed Escape, or the prompt closed itself. Only a real fault is shown.
 */
export function isWebAuthnCancel(err: unknown): boolean {
  if (err instanceof WebAuthnUnsupportedError) return true;
  const name = (err as { name?: string } | null)?.name;
  return name === "NotAllowedError" || name === "AbortError" || name === "InvalidStateError";
}

/** A default label for a new key, so the list is readable without typing one. */
function deviceLabel(): string {
  const ua = typeof navigator === "undefined" ? "" : navigator.userAgent;
  if (/Mobile|Android|iPhone|iPad/.test(ua)) return "Mobiil";
  if (/Mac OS X|Macintosh/.test(ua)) return "Mac";
  if (/Windows/.test(ua)) return "Windows";
  return "Brauser";
}

export const passkeyApi = {
  list: () => api.get<{ passkeys: Passkey[] }>("/auth/passkey/list"),
  remove: (id: string) => api.del(`/auth/passkey/${id}`),

  /** Enrol a new passkey for this domain (needs a signed-in session). */
  async register(): Promise<void> {
    if (!isWebAuthnReady()) throw new WebAuthnUnsupportedError("webauthn_unsupported");
    const options = await api.post<Parameters<typeof startRegistration>[0]>(
      "/auth/passkey/register/begin",
      {},
    );
    const response = await startRegistration(options);
    await api.post("/auth/passkey/register/complete", { response, deviceName: deviceLabel() });
  },

  /**
   * Passkey sign-in. With an email the server can send `allowCredentials`, so
   * the browser goes straight to Touch ID instead of showing a key picker.
   * Returns a session or the MFA gate, exactly like a password login.
   */
  async login(email?: string): Promise<LoginResult> {
    if (!isWebAuthnReady()) throw new WebAuthnUnsupportedError("webauthn_unsupported");
    const options = await api.post<Parameters<typeof startAuthentication>[0]>(
      "/auth/passkey/login/begin",
      email ? { email } : {},
    );
    const response = await startAuthentication(options);
    return api.post<LoginResult>("/auth/passkey/login/complete", { response });
  },
};
